import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LevelData } from '../data/curriculum';
import { useTTS } from '../hooks/useTTS';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { matchesFactSpeech } from '../utils/speechMatcher';
import { playSuccessSound } from '../utils/sounds';
import { factToSpeechText } from '../data/audioTextGenerator';
import FactDisplay from '../components/FactDisplay';
import CountdownTimer from '../components/CountdownTimer';
import MicrophoneIndicator from '../components/MicrophoneIndicator';
import { motion, AnimatePresence } from 'framer-motion';
import TTSPlaybackIndicator from '../components/TTSPlaybackIndicator';

interface Step4SeriesSpeakingProps {
  level: LevelData;
  onComplete: () => void;
}

type FactPhase = 'tts_playing' | 'countdown' | 'recording' | 'transcribing' | 'success' | 'retry' | 'max_retries';

const MAX_RETRIES_PER_FACT = 3;

const Step4SeriesSpeaking: React.FC<Step4SeriesSpeakingProps> = ({ level, onComplete }) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [factPhase, setFactPhase] = useState<FactPhase>('tts_playing');
  const [retryCount, setRetryCount] = useState(0);
  const [completedFacts, setCompletedFacts] = useState<number[]>([]);
  const { playText, isLoading, isPlaying, error: ttsError } = useTTS();
  const { startListening, isRecording, isTranscribing, micPermissionDenied, volume } = useSpeechRecognition();
  const hasStartedTTS = useRef(false);

  const currentFact = level.primaryFacts[currentFactIndex];
  const expectedText = currentFact ? factToSpeechText(currentFact) : '';

  // Play TTS for current fact
  useEffect(() => {
    if (factPhase !== 'tts_playing' || hasStartedTTS.current) return;
    hasStartedTTS.current = true;

    const play = async () => {
      await playText(expectedText);
      await playText(currentFactIndex === 0 ? "Now it's your turn! Say it out loud." : "Your turn");
      setFactPhase('countdown');
    };
    play();
  }, [factPhase, expectedText, playText, currentFactIndex]);

  // Auto-advance on success only
  useEffect(() => {
    if (factPhase === 'success') {
      const timer = setTimeout(() => {
        setCompletedFacts((prev) => [...prev, currentFactIndex]);

        if (currentFactIndex < level.primaryFacts.length - 1) {
          setCurrentFactIndex((prev) => prev + 1);
          setRetryCount(0);
          setFactPhase('tts_playing');
          hasStartedTTS.current = false;
        } else {
          onComplete();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [factPhase, currentFactIndex, level.primaryFacts.length, onComplete]);

  // Auto-advance from retry back to countdown
  useEffect(() => {
    if (factPhase === 'retry') {
      const timer = setTimeout(() => setFactPhase('countdown'), 1500);
      return () => clearTimeout(timer);
    }
  }, [factPhase]);

  // Handle mic error — auto-advance all remaining facts
  useEffect(() => {
    if (micPermissionDenied && factPhase === 'recording') {
      onComplete();
    }
  }, [micPermissionDenied, factPhase, onComplete]);

  const handleCountdownComplete = useCallback(async () => {
    setFactPhase('recording');

    const { transcript, micDenied } = await startListening();

    if (micDenied) {
      onComplete();
      return;
    }

    setFactPhase('transcribing');

    if (matchesFactSpeech(transcript, expectedText)) {
      playSuccessSound();
      setFactPhase('success');
    } else {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      if (newRetryCount >= MAX_RETRIES_PER_FACT) {
        setFactPhase('max_retries');
      } else {
        setFactPhase('retry');
      }
    }
  }, [startListening, expectedText, retryCount, onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-8 rounded-2xl shadow-lg text-center max-w-lg w-full"
    >

      {/* Ladder of completed facts + current */}
      <div className="flex flex-col items-center gap-3 mb-6">
        {level.primaryFacts.map((fact, idx) => {
          if (idx > currentFactIndex) return null;
          const isCurrent = idx === currentFactIndex;
          const isCompleted = completedFacts.includes(idx);

          return (
            <motion.div
              key={`${fact.addend1}_${fact.addend2}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: isCurrent ? 1 : 0.45,
                x: 0,
                scale: isCurrent ? 1.1 : 0.9,
              }}
              transition={{ duration: 0.3 }}
              className="flex items-center gap-2"
            >
              <FactDisplay fact={fact} showAnswer={true} />
              {isCompleted && !isCurrent && (
                <span className="text-green-500 text-sm">&#10003;</span>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Phase-specific UI */}
      <AnimatePresence mode="wait">
        {factPhase === 'tts_playing' && (
          <motion.div key="tts" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TTSPlaybackIndicator isLoading={isLoading} isPlaying={isPlaying} error={ttsError} />
          </motion.div>
        )}

        {factPhase === 'countdown' && (
          <motion.div key="countdown" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <p className="text-gray-500 text-sm mb-2">Your turn!</p>
            <CountdownTimer onComplete={handleCountdownComplete} />
          </motion.div>
        )}

        {(factPhase === 'recording' || factPhase === 'transcribing') && (
          <motion.div key="recording" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <MicrophoneIndicator isRecording={isRecording} isTranscribing={isTranscribing} volume={volume} />
          </motion.div>
        )}

        {factPhase === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-green-500 font-bold text-lg"
          >
            Correct!
          </motion.div>
        )}

        {factPhase === 'retry' && (
          <motion.div
            key="retry"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="text-amber-500 font-bold text-lg"
          >
            Let's try again!
          </motion.div>
        )}

        {factPhase === 'max_retries' && (
          <motion.div
            key="max_retries"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3"
          >
            <p className="text-indigo-500 font-medium text-lg">Let's try again when you're ready!</p>
            <button
              onClick={() => { setRetryCount(0); setFactPhase('countdown'); }}
              className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Try Again
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Step4SeriesSpeaking;
